import {
  ApplicantArticle,
  ApplicantAward,
  ApplicantCommunity,
  ApplicantCompany,
  ApplicantContest,
  ApplicantEntrepreneurship,
  ApplicantMentionInMedia,
  ApplicantMentorship,
  ApplicantPatent,
  ApplicantPublicTalk,
  ApplicantScientificWork,
} from "@prisma/client";
import Blob from "cross-blob";
import {
  Document,
  HeadingLevel,
  ISectionOptions,
  Packer,
  Paragraph,
  TextRun,
} from "docx";
import flattenDeep from "lodash/flattenDeep";

import { ExperienceType } from "@/lib/experienceType";

import { docStyles, renderProofList } from "./common";
import { numberingStyle } from "./common/numbering";
import { DocGenerator } from "../";
import { parseProseMirrorSchema } from "../../proseMirror";

interface SelfServiceCheckListDocArgs {
  applicantCompanyList: ApplicantCompany[];
  applicantEntrepreneurshipList: ApplicantEntrepreneurship[];
  applicantArticleList: ApplicantArticle[];
  applicantPublicTalkList: ApplicantPublicTalk[];
  applicantContestList: ApplicantContest[];
  applicantAwardList: ApplicantAward[];
  applicantPatentList: ApplicantPatent[];
  applicantMentorshipList: ApplicantMentorship[];
  applicantCommunityList: ApplicantCommunity[];
  applicantScientificWorkList: ApplicantScientificWork[];
  applicantMentionInMediaList: ApplicantMentionInMedia[];
}

export const createSelfServiceCheckListDoc: DocGenerator<
  SelfServiceCheckListDocArgs
> = async (
  t,
  {
    applicantCompanyList,
    applicantEntrepreneurshipList,
    applicantArticleList,
    applicantPublicTalkList,
    applicantContestList,
    applicantAwardList,
    applicantPatentList,
    applicantMentorshipList,
    applicantCommunityList,
    applicantScientificWorkList,
    applicantMentionInMediaList,
  }
) => {
  const document = new Document({
    styles: docStyles,
    numbering: numberingStyle,
    sections: [
      {
        children: flattenDeep<ISectionOptions["children"][0] | null>([
          new Paragraph({
            text: "Documents check list",
            heading: HeadingLevel.HEADING_1,
          }),
          [
            {
              name: ExperienceType.PublicTalks,
              items: applicantPublicTalkList,
            },
            {
              name: ExperienceType.Articles,
              items: applicantArticleList,
            },
            {
              name: ExperienceType.Contests,
              items: applicantContestList,
            },
            {
              name: ExperienceType.TeachingAndMentorship,
              items: applicantMentorshipList,
            },
            {
              name: ExperienceType.ScientificWorkOrPhD,
              items: applicantScientificWorkList,
            },
            {
              name: ExperienceType.Award,
              items: applicantAwardList,
            },
            {
              name: ExperienceType.CommunityDevelopment,
              items: applicantCommunityList,
            },
            {
              name: ExperienceType.Patent,
              items: applicantPatentList,
            },
            {
              name: ExperienceType.MentionInMedia,
              items: applicantMentionInMediaList,
            },
          ].map(({ name, items }, i) =>
            items.length
              ? [
                  new Paragraph({
                    text: name,
                    heading: HeadingLevel.HEADING_2,
                  }),
                  items.map((item, j) => [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: item.name
                            ? parseProseMirrorSchema(item.name)
                            : "-",
                          bold: true,
                        }),
                      ],
                    }),
                    renderProofList(t, item.proof, i * 100 + j),
                    new Paragraph({
                      style: "wellSpaced",
                    }),
                  ]),
                ]
              : null
          ),

          [
            {
              name: ExperienceType.Employment,
              items: applicantCompanyList,
            },
            {
              name: ExperienceType.Entrepreneurship,
              items: applicantEntrepreneurshipList,
            },
          ].map(({ name, items }, i) =>
            items.length
              ? [
                  new Paragraph({
                    text: name,
                    heading: HeadingLevel.HEADING_2,
                  }),
                  items.map((item, j) => [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: item.name
                            ? parseProseMirrorSchema(item.name)
                            : "-",
                          bold: true,
                        }),
                      ],
                    }),
                    renderProofList(t, item.proof, i * 100 + j),
                    new Paragraph({
                      style: "wellSpaced",
                    }),
                  ]),
                ]
              : null
          ),
        ]).filter((x): x is ISectionOptions["children"][0] => x !== null),
      },
    ],
  });

  return new Blob([await Packer.toBuffer(document)]);
};
